/**
 * Mitarbeiter der Lohnverrechnung (AP-38-F).
 *
 * Im Betrieb „nur Lohnverrechnung" melden sich die meisten Mitarbeiter nie an:
 * Ihre Monatslisten werden abgetippt. Bis AP-38-F brauchte trotzdem jeder eine
 * E-Mail-Adresse und eine Einladung. Hier legt die Lohnverrechnung sie als
 * **Mitarbeiter ohne Zugang** an — die Adresse ist freiwillig — und pflegt Name
 * und Adresse. Wer einen Zugang hat, pflegt beides selbst im Profil; die Zeile
 * ist dann gesperrt. Einen Zugang gibt die Benutzerverwaltung.
 *
 * AP-36: Zeilenblock wie die Fehlzeit-Arten. Jede Zeile ist eine Speicherstelle
 * (`payrollEmployee:<id>`), die leere letzte Zeile legt an.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { erfuelltRecht } from '@symphenia/shared-types';
import { api } from '@/lib/axios';
import { useAuthStore } from '@/lib/auth.store';
import { apiErrorText } from '@/lib/api-error';
import { pageWide } from '@/lib/form-styles';
import { ZeilenBlock, ZeilenListe, Feld as Spalte, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock';
import { mitStand, useFeld, useGruppe, useSpeicherstelle, type Datensatz } from '@/lib/speichern';
import { createPayrollEmployee, fetchLatestSlips, fetchPayrollEmployees, fetchPersonnelNumbers } from '../api/payroll.api';
import type { PayrollEmployee } from '../types/payroll.types';

const SPALTEN: SpaltenDef[] = [
  { key: 'lastName', label: 'Nachname', spur: 'minmax(8rem,2fr)', min: 120, schmal: 'w-full' },
  { key: 'firstName', label: 'Vorname', spur: 'minmax(8rem,2fr)', min: 120, schmal: 'w-full' },
  { key: 'email', label: 'E-Mail (freiwillig)', spur: 'minmax(10rem,3fr)', min: 160, schmal: 'w-full' },
  { key: 'personalnummer', label: 'Personalnr. (BMD)', spur: '9rem', min: 110, schmal: 'w-32' },
  { key: 'lohn', label: 'Lohnverrechnung', spur: '8.5rem', min: 110, schmal: 'w-32' },
  { key: 'zettel', label: 'Abgabezettel', spur: '8rem', min: 100, schmal: 'w-32' },
  { key: 'zugang', label: '', spur: '7.5rem', min: 90, schmal: 'w-28' },
];

/**
 * Das PATCH schreibt flach, die Liste liest das Profil verschachtelt. Die Stelle
 * schickt **beides** hier durch — die geladene Zeile (`daten`) und die Antwort
 * des PATCH (`{ data }`); wer `daten` schon flach hereingibt, flacht zweimal ab.
 */
function flach(antwort: unknown): Datensatz {
  const m = ((antwort as { data?: PayrollEmployee }).data ?? antwort) as PayrollEmployee;
  return {
    firstName: m.profile?.firstName ?? '',
    lastName: m.profile?.lastName ?? '',
    email: m.email,
    updatedAt: m.updatedAt,
  };
}

export default function PayrollEmployeesPage() {
  const authUser = useAuthStore((s) => s.user);
  const darfPflegen = erfuelltRecht('payroll.employee.manage', authUser?.permissions);
  const [rows, setRows] = useState<PayrollEmployee[]>([]);
  const [geladen, setGeladen] = useState(false);
  const [auchInaktive, setAuchInaktive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // H: der zuletzt hinterlegte Abgabezettel je Mitarbeiter — nur mit dem Recht, Zettel zu sehen.
  const darfZettel = erfuelltRecht('payroll.slip.read', authUser?.permissions);
  const [zettel, setZettel] = useState<Map<string, { jahr: number; monat: number }>>(new Map());
  useEffect(() => {
    if (!darfZettel) return;
    fetchLatestSlips()
      .then((liste) => setZettel(new Map(liste.map((z) => [z.userId, { jahr: z.jahr, monat: z.monat }]))))
      .catch(() => setZettel(new Map()));
  }, [darfZettel]);

  // F: die Personalnummer, wie sie in BMD steht — eigene Angabe der Lohnverrechnung, auch bei Mitarbeitern mit Zugang.
  // Dazu „keine Lohnverrechnung" (Administrator, Geschäftsführer): Übersicht, Erfassung und Auswertung blenden ihn aus.
  const [nummern, setNummern] = useState<Map<string, LohnDaten> | null>(null);
  useEffect(() => {
    fetchPersonnelNumbers()
      .then((liste) => setNummern(new Map(liste.map((n) => [n.userId, n]))))
      .catch(() => setNummern(new Map()));
  }, []);

  const fetchRows = useCallback(async () => {
    setError(null);
    try {
      const liste = await fetchPayrollEmployees(auchInaktive ? {} : { isActive: true });
      setRows(
        liste
          .slice()
          .sort((a, b) =>
            [a.profile?.lastName, a.profile?.firstName].join(' ').localeCompare([b.profile?.lastName, b.profile?.firstName].join(' '), 'de'),
          ),
      );
    } catch (err) {
      setError(apiErrorText(err, 'Die Mitarbeiter konnten nicht geladen werden.'));
    } finally {
      setGeladen(true);
    }
  }, [auchInaktive]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const ohne = rows.filter((r) => r.ohneZugang).length;
  const ohneLohn = nummern ? rows.filter((r) => nummern.get(r.id)?.noPayroll).length : 0;

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Mitarbeiter</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Wessen Lohn verrechnet wird. Ein Mitarbeiter ohne Zugang meldet sich nie an — seinen Namen und seine
            Adresse pflegst du hier; eine Zeile wird gespeichert, sobald du sie verlässt. Nimmst du bei
            „Lohnverrechnung" das Häkchen weg, blenden Monatsübersicht, Erfassung und Auswertung ihn aus.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={auchInaktive} onChange={(e) => setAuchInaktive(e.target.checked)} />
          auch ausgeschiedene
        </label>
      </div>

      {error && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

      <ZeilenListe spalten={SPALTEN}>
        {!geladen && <p className="px-4 py-6 text-center text-sm text-muted-foreground">Laden…</p>}
        {geladen && rows.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            Noch keine Mitarbeiter.{darfPflegen && ' Leg in der Zeile unten den ersten an.'}
          </p>
        )}
        {nummern && rows.map((m) => (
          <MitarbeiterZeile
            key={m.id}
            mitarbeiter={m}
            gesperrt={!darfPflegen || !m.ohneZugang}
            zettel={zettel.get(m.id) ?? null}
            personalnummer={nummern.get(m.id) ?? { personnelNumber: null, noPayroll: false, updatedAt: null }}
            nummerGesperrt={!darfPflegen}
          />
        ))}
        {darfPflegen && <NeuerMitarbeiter onAngelegt={() => void fetchRows()} onFehler={setError} />}
      </ZeilenListe>

      <p className="text-xs text-muted-foreground">
        {rows.length} Mitarbeiter, davon {ohne} ohne Zugang{ohneLohn > 0 && ` und ${ohneLohn} ohne Lohnverrechnung`}.{' '}
        <KeyRound size={12} className="inline align-[-1px]" /> — hat einen Zugang und pflegt Name und Adresse selbst im
        Profil. Einen Zugang gibt die Benutzerverwaltung („Zugang geben").
      </p>
    </div>
  );
}

/** Was die Lohnverrechnung je Mitarbeiter selbst führt — eine Speicherstelle, nicht Teil des Profils. */
interface LohnDaten {
  personnelNumber: string | null;
  noPayroll: boolean;
  updatedAt: string | null;
}

function MitarbeiterZeile({
  mitarbeiter,
  gesperrt,
  zettel,
  personalnummer,
  nummerGesperrt,
}: {
  mitarbeiter: PayrollEmployee;
  gesperrt: boolean;
  /** Der Monat des zuletzt hinterlegten Abgabezettels (AP-22 Teil 2, H). */
  zettel: { jahr: number; monat: number } | null;
  /** Die Personalnummer aus BMD (AP-22 Teil 2, F) und „keine Lohnverrechnung". */
  personalnummer: LohnDaten;
  nummerGesperrt: boolean;
}) {
  const bezeichnung = [mitarbeiter.profile?.firstName, mitarbeiter.profile?.lastName].filter(Boolean).join(' ') || 'Mitarbeiter';
  const stelle = useSpeicherstelle<unknown>({
    schluessel: `payrollEmployee:${mitarbeiter.id}`,
    bezeichnung,
    daten: mitarbeiter,
    werte: flach,
    schreibe: (patch, stand) =>
      api.patch<unknown>(`/payroll/employees/${mitarbeiter.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/payroll/employees/${mitarbeiter.id}`, method: 'PATCH' },
  });
  const gruppe = useGruppe(stelle, { label: bezeichnung });
  const nachname = useFeld(stelle, 'lastName', { gruppe, pflicht: true, label: 'Nachname', gesperrt });
  const vorname = useFeld(stelle, 'firstName', { gruppe, pflicht: true, label: 'Vorname', gesperrt });
  const email = useFeld<string | null>(stelle, 'email', {
    gruppe,
    label: 'E-Mail',
    gesperrt,
    pruefe: (w) => (w && !/^\S+@\S+\.\S+$/.test(w) ? 'Keine gültige E-Mail-Adresse' : null),
  });
  // Die Personalnummer gehört der Lohnverrechnung, nicht dem Profil: Sie bleibt auch
  // pflegbar, wenn der Mitarbeiter einen Zugang hat und seine Zeile sonst gesperrt ist.
  const nummerStelle = useSpeicherstelle<unknown>({
    schluessel: `payrollPersonnelNumber:${mitarbeiter.id}`,
    bezeichnung: `Personalnummer ${bezeichnung}`,
    daten: personalnummer,
    schreibe: (patch, stand) =>
      api.put<unknown>(`/payroll/bmd/personnel-numbers/${mitarbeiter.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/payroll/bmd/personnel-numbers/${mitarbeiter.id}`, method: 'PUT' },
  });
  // Bewusst ohne `gruppe`: Die Zeilengruppe schreibt ins Profil, dieses Feld für sich.
  const nummer = useFeld<string | null>(nummerStelle, 'personnelNumber', {
    label: 'Personalnummer',
    gesperrt: nummerGesperrt,
  });
  // Gespeichert wird „keine Lohnverrechnung", gezeigt das Gegenteil: Häkchen = Lohn wird verrechnet.
  const ohneLohn = useFeld<boolean>(nummerStelle, 'noPayroll', {
    art: 'schalter',
    label: 'Lohnverrechnung',
    gesperrt: nummerGesperrt,
  });

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={mitarbeiter.id}>
      <Spalte spalte="lastName">
        <input aria-label="Nachname" className={`${zelle} font-medium`} data-start {...nachname.props} />
      </Spalte>
      <Spalte spalte="firstName">
        <input aria-label="Vorname" className={zelle} {...vorname.props} />
      </Spalte>
      <Spalte spalte="email">
        <input aria-label="E-Mail" type="email" className={zelle} {...email.props} />
      </Spalte>
      <Spalte spalte="personalnummer">
        <input aria-label="Personalnummer in BMD" maxLength={30} className={`${zelle} tabular-nums`} {...nummer.props} />
      </Spalte>
      <Spalte spalte="lohn">
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            aria-label="Lohnverrechnung"
            className="h-4 w-4 accent-[hsl(var(--primary))]"
            {...ohneLohn.schalter}
            checked={!ohneLohn.schalter.checked}
            onChange={(e) => ohneLohn.setze(!e.target.checked)}
          />
          {ohneLohn.schalter.checked ? 'keine' : 'ja'}
        </label>
      </Spalte>
      <Spalte spalte="zettel">
        {zettel ? (
          <Link
            to={`/payroll/entry?user=${mitarbeiter.id}&month=${zettel.jahr}-${String(zettel.monat).padStart(2, '0')}`}
            className="text-xs text-primary hover:underline"
            title="Zum Monat mit dem zuletzt hinterlegten Abgabezettel"
          >
            {String(zettel.monat).padStart(2, '0')}/{zettel.jahr}
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </Spalte>
      <Spalte spalte="zugang">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {mitarbeiter.ohneZugang ? (
            'ohne Zugang'
          ) : (
            <>
              <KeyRound size={13} aria-hidden="true" /> mit Zugang
            </>
          )}
          {!mitarbeiter.isActive && <span className="rounded bg-muted px-1">ausgeschieden</span>}
        </span>
      </Spalte>
    </ZeilenBlock>
  );
}

/** Die leere letzte Zeile: legt beim Verlassen an, sobald Vor- und Nachname dastehen. */
function NeuerMitarbeiter({ onAngelegt, onFehler }: { onAngelegt: () => void; onFehler: (t: string | null) => void }) {
  const leer = { firstName: '', lastName: '', email: '' };
  const [neu, setNeu] = useState(leer);

  const anlegen = async () => {
    if (!neu.firstName.trim() || !neu.lastName.trim()) return;
    try {
      onFehler(null);
      await createPayrollEmployee({
        firstName: neu.firstName.trim(),
        lastName: neu.lastName.trim(),
        email: neu.email.trim() || null,
      });
      setNeu(leer);
      onAngelegt();
    } catch (err) {
      onFehler(apiErrorText(err, 'Der Mitarbeiter konnte nicht angelegt werden.'));
    }
  };

  return (
    <ZeilenBlock neu onVerlassen={() => void anlegen()} onVerwerfen={() => setNeu(leer)}>
      <Spalte spalte="lastName">
        <input
          aria-label="Nachname des neuen Mitarbeiters"
          className={zelle}
          placeholder="Nachname"
          value={neu.lastName}
          onChange={(e) => setNeu((n) => ({ ...n, lastName: e.target.value }))}
        />
      </Spalte>
      <Spalte spalte="firstName">
        <input
          aria-label="Vorname des neuen Mitarbeiters"
          className={zelle}
          placeholder="Vorname"
          value={neu.firstName}
          onChange={(e) => setNeu((n) => ({ ...n, firstName: e.target.value }))}
        />
      </Spalte>
      <Spalte spalte="email">
        <input
          aria-label="E-Mail des neuen Mitarbeiters"
          type="email"
          className={zelle}
          placeholder="freiwillig"
          value={neu.email}
          onChange={(e) => setNeu((n) => ({ ...n, email: e.target.value }))}
        />
      </Spalte>
      <Spalte spalte="personalnummer" leer>
        <span />
      </Spalte>
      <Spalte spalte="lohn" leer>
        <span />
      </Spalte>
      <Spalte spalte="zettel" leer>
        <span />
      </Spalte>
      <Spalte spalte="zugang" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  );
}
